// @flow

import React from 'react';
import bemify from 'bemify-js';

import { isEmptyString } from '@zapier/common-utils';

import FolderChooser from 'app/dashboard/components/FolderChooser';
import Menu from 'app/common/components/Menu';
import SearchBox from 'app/common/components/SearchBox';

import SearchableFolderChooserLoader from './SearchableFolderChooserLoader';

import type { FolderChooserProps } from 'app/dashboard/components/FolderChooser';

const bem = bemify('searchable-folder-chooser');

type FolderCreatorProps = {
  folderName: string,
  isCreating: boolean,
  onCreate: (folderName: string) => Promise<any> | void,
};

// Even though there's only a single item in this `Menu`, use
// `Menu` since we get a lot of useful styles that come with it.
// If we used `button` then we'd have to keep the styles *nsync.
const FolderCreator = (props: FolderCreatorProps) => {
  const onCreate = (folderName: string, didConfirm: boolean) => {
    if (didConfirm) {
      props.onCreate(folderName);
    }
  };
  const choices = [
    {
      isDisabled: props.isCreating,
      label: `${props.isCreating ? 'Creating…' : 'Create'} “${
        props.folderName
      }”`,
      value: props.folderName,
    },
  ];
  return (
    <Menu
      items={choices}
      kind="borderless"
      listKind="dominant"
      onChoose={onCreate}
    />
  );
};

type SearchableFolderChooserProps = {
  ...FolderChooserProps,
  isCreating: boolean,
  isLoading: boolean,
  query: string,
  onChangeQuery: (query: string) => void,
  onCreateFolder: (newFolderTitle: string) => Promise<any> | void,
  onResetQuery: () => void,
};

const SearchableFolderChooser = ({
  isCreating,
  isLoading,
  query,
  onChangeQuery,
  onCreateFolder,
  onResetQuery,
  ...props
}: SearchableFolderChooserProps) => {
  const createFolder = !isEmptyString(query) && (
    <FolderCreator
      folderName={query.trim()}
      isCreating={isCreating}
      onCreate={onCreateFolder}
    />
  );
  return isLoading ? (
    <SearchableFolderChooserLoader />
  ) : (
    <div className={bem()}>
      <SearchBox
        autoFocus={true}
        kind="flat-bottom bottom-border-only"
        name="folder-chooser"
        onChangeValue={onChangeQuery}
        onReset={onResetQuery}
        placeholder="Search folders…"
        value={query}
      />
      {createFolder}
      <FolderChooser {...props} />
    </div>
  );
};

export default SearchableFolderChooser;
